import { useSyncExternalStore } from "react";
import {
  localeTag,
  resolveLocale,
  translate,
  type Locale,
} from "../shared/translations";

const storageKey = "ranna-language";
function initialLocale(): Locale {
  if (typeof window === "undefined") return "en";
  let saved: string | null = null;
  try {
    saved = localStorage.getItem(storageKey);
  } catch {
    /* Storage may be disabled. */
  }
  return resolveLocale(
    new URLSearchParams(location.search).get("lang"),
    saved,
    navigator.languages,
  );
}
let locale = initialLocale();
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
export const t = (source: string, params?: Record<string, string | number>) =>
  translate(source, locale, params);
export const number = (value: number) =>
  value.toLocaleString(localeTag(locale));
export const time = (value: number) =>
  new Intl.DateTimeFormat(localeTag(locale), {
    hour: "numeric",
    minute: "2-digit",
  }).format(value);
export function day(value: number, short = false) {
  const date = new Date(value),
    today = new Date(),
    yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return t("اليوم");
  if (date.toDateString() === yesterday.toDateString()) return t("أمس");
  return new Intl.DateTimeFormat(localeTag(locale), {
    day: "numeric",
    month: short ? "short" : "long",
    ...(date.getFullYear() !== today.getFullYear()
      ? { year: "numeric" as const }
      : {}),
  }).format(date);
}
export const date = (value: number) =>
  new Intl.DateTimeFormat(localeTag(locale), { dateStyle: "long" }).format(
    value,
  );
export const dateTime = (value: number) =>
  new Intl.DateTimeFormat(localeTag(locale), {
    dateStyle: "long",
    timeStyle: "short",
  }).format(value);
// How long a call lasted: 0:03, 4:12, 1:02:33.
export function duration(seconds: number) {
  const digits = new Intl.NumberFormat(localeTag(locale), {
      minimumIntegerDigits: 2,
    }),
    minutes = Math.floor(seconds / 60),
    rest = digits.format(seconds % 60);
  return minutes < 60
    ? `${number(minutes)}:${rest}`
    : `${number(Math.floor(minutes / 60))}:${digits.format(minutes % 60)}:${rest}`;
}

function updateDocument() {
  if (typeof document === "undefined") return;
  document.documentElement.lang = locale;
  document.documentElement.dir = locale === "ar" ? "rtl" : "ltr";
  document.title = t("رنّة | على الخط");
  for (const selector of [
    'meta[property="og:title"]',
    'meta[name="twitter:title"]',
  ])
    document.querySelector(selector)?.setAttribute("content", document.title);
  document
    .querySelector('meta[property="og:locale"]')
    ?.setAttribute("content", locale === "ar" ? "ar_EG" : "en_US");
  document
    .querySelector('meta[name="description"]')
    ?.setAttribute(
      "content",
      t("أنشئ رابطًا وأرسله لأي شخص، وتحدثا بالصوت أو الفيديو عندما يفتحه."),
    );
  const description = document
    .querySelector('meta[name="description"]')
    ?.getAttribute("content");
  if (description) {
    for (const selector of [
      'meta[property="og:description"]',
      'meta[name="twitter:description"]',
    ])
      document.querySelector(selector)?.setAttribute("content", description);
  }
}
export function setLocale(next: Locale) {
  locale = next;
  try {
    localStorage.setItem(storageKey, next);
  } catch {
    /* The selection still works in memory. */
  }
  updateDocument();
  listeners.forEach((listener) => listener());
}
export const useLocale = () =>
  useSyncExternalStore(
    subscribe,
    () => locale,
    () => "en" as Locale,
  );
updateDocument();
